import { useSelector } from "react-redux";
import { isValidNumber } from "src/components/core/IntlTelInput/IntlTelInput";
import i18n from "src/locales";
import { isNursing } from "src/utils/pace/planTimeAdjustmentNursing";
import {
  CollapsedEnrollmentData,
  requestEnrollment,
} from "src/utils/api/enrollment";
import { trackEvent } from "src/utils/api/tracker";
import { getLanguage } from "src/utils/meristemContext";
import { prepareDataForBackend } from "src/utils/pace";
import { shouldAskForPhoneNumber } from "src/utils/payment";
import { CoreReduxState } from "src/utils/redux/internal";
import { updateCheckoutState } from "src/utils/redux/slices/checkout";
import {
  EnrollmentErrors,
  PaymentEnrollmentFormState,
  updatePaymentEnrollmentFormState,
} from "src/utils/redux/slices/paymentEnrollmentForm";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { genderConstantsMapping } from "src/utils/sexAndGender";
import { isHM, isUS } from "src/utils/userSegment";
import {
  isCollapsedPaymentEligible,
  hasExistingAcccount,
  isMultiUserPlanEligible,
  acceptedNoomClinicalOffer,
} from "src/utils/userSegment/features";
import { isEmail, isName } from "src/utils/validate";
import { useCallbackRef, useOnce } from "./lifecycle";
import {
  useAppDispatch,
  useCheckout,
  useCoreState,
  usePromoCode,
  useUserData,
} from "./redux";
import { useSurveyAnswers } from "./survey/answers";
import { useBillingForm } from "./billing-info";
import { useEffect } from "react";
import { format } from "@utils/datetime";
import { failNoomClinicalAgeValidation } from "src/utils/redux/slices/paymentEnrollmentFormActions";

// Removes name field
// Adds first name, last name, date of birth and sms consent fields
// Currently used if the user has Noom Clinical added on checkout
export function useNoomClinicalEnrollmentFormFields() {
  const state = useCoreState();
  return acceptedNoomClinicalOffer(state);
}

export function useEnrollmentForm() {
  const checkout = useCheckout();
  const promoCode = usePromoCode();
  const enrollmentForm = useSelector(
    (state: CoreReduxState) => state.paymentEnrollmentForm
  );
  const {
    enrollmentInfo,
    enrollmentErrors = {},
    showInvalidAgeModal,
  } = enrollmentForm;
  const hasEnrollmentErrors = Object.values(enrollmentErrors).some(Boolean);
  const { billingNameChangeMade, updateBillingName } = useBillingForm();
  const useNoomClinicalFields = useNoomClinicalEnrollmentFormFields();

  const dispatch = useAppDispatch();

  function updateEnrollmentInfo(
    newEnrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"]
  ) {
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentInfo: newEnrollmentInfo,
        enrollmentErrors: validateEnrollmentInfo(
          newEnrollmentInfo,
          useNoomClinicalFields
        ),
      })
    );
  }

  function parseNames(name: string) {
    const names = name.split(" ");
    const firstName = names[0] || "";
    const lastName = names[names.length - 1] || "";
    return [firstName, lastName];
  }

  // TODO: See if there is a better way to seed this
  // Set default values and default errors for enrollment form
  const userData = useUserData();
  useOnce(() => {
    if (!enrollmentInfo.name && !enrollmentInfo.email) {
      let firstName;
      let lastName;
      if (userData.name) {
        [firstName, lastName] = parseNames(userData.name);
      }

      const newEnrollmentInfo = {
        ...enrollmentInfo,
        name: userData.name,
        email: userData.email,
        firstName,
        lastName,
      };

      updateEnrollmentInfo(newEnrollmentInfo);
    }
  });

  useEffect(() => {
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentErrors: validateEnrollmentInfo(
          enrollmentInfo,
          useNoomClinicalFields
        ),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useNoomClinicalFields]);

  function setField(
    values: Omit<
      Partial<PaymentEnrollmentFormState["enrollmentInfo"]>,
      "termAgreement"
    >
  ) {
    const newEnrollmentInfo = {
      ...enrollmentInfo,
      ...values,
    };

    if ("dateOfBirth" in values) {
      const oldDob = enrollmentInfo.dateOfBirth || "";
      const newDob = values.dateOfBirth || "";

      // If user deleted a character, automatically remove trailing slash /
      if (newDob.length < oldDob.length && newDob[newDob.length - 1] === "/") {
        newEnrollmentInfo.dateOfBirth = newDob.slice(0, newDob.length - 1);
      }
      // If user added a character
      else if (newDob.length > oldDob.length) {
        // Remove non numeric characters and ensure we only have 8 numbers max
        const rawDob = newDob.replace(/[^0-9]/g, "").slice(0, 8);
        const parts = [];
        if (rawDob.length) {
          // month
          parts.push(rawDob.slice(0, 2));
        }
        if (rawDob.length >= 2) {
          // day
          parts.push(rawDob.slice(2, 4));
        }
        if (rawDob.length >= 4) {
          // year
          parts.push(rawDob.slice(4));
        }
        // Automatically add a slash between month and day, day and year to ensure the format dd/mm/yyyy
        newEnrollmentInfo.dateOfBirth = parts.join("/");
      }
    }

    ["password", "confirmPassword", "email"].forEach((field) => {
      if (field in values) {
        newEnrollmentInfo[field] = values[field].replace(/\s/g, "");
      }
    });

    if ("name" in values) {
      const [firstName, lastName] = parseNames(values.name);

      // Keep first and last name in sync in case
      // the user decides to add Noom Clinical later
      newEnrollmentInfo.firstName = firstName;
      newEnrollmentInfo.lastName = lastName;
      if (!billingNameChangeMade) {
        updateBillingName(values.name, false);
      }
    } else if ("firstName" in values || "lastName" in values) {
      const firstName = newEnrollmentInfo.firstName || "";
      const lastName = newEnrollmentInfo.lastName || "";

      const newName =
        firstName && lastName
          ? `${firstName} ${lastName}`
          : `${firstName}${lastName}`;

      // Keep the name value in sync even when hidden since
      // we still need to send it to the backend
      newEnrollmentInfo.name = newName;
      if (!billingNameChangeMade) {
        updateBillingName(newName, false);
      }
    }

    updateEnrollmentInfo(newEnrollmentInfo);
    dispatch(updateCheckoutState({ userStartedEnteringPaymentInfo: true }));
  }

  function hasVisibleError(field?: keyof EnrollmentErrors) {
    const { showPaymentErrors } = checkout;

    if (!showPaymentErrors) {
      return false;
    }

    return field ? !!enrollmentErrors[field] : hasEnrollmentErrors;
  }

  function setTermAgreement(termAgreement: boolean) {
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentInfo: {
          ...enrollmentInfo,
          termAgreement,
        },
        enrollmentErrors: {
          ...enrollmentErrors,
          // Will be re-evaled on submit
          termAgreement: "",
        },
      })
    );
  }
  function validateTermAgreement() {
    if (!promoCode.promoCodeIsVip && isUS() && !enrollmentInfo.termAgreement) {
      const errorMessage = i18n.t("payment:terms:error");
      dispatch(
        updatePaymentEnrollmentFormState({
          enrollmentErrors: {
            ...enrollmentErrors,
            termAgreement: errorMessage,
          },
        })
      );
      return {
        termAgreement: errorMessage,
      };
    }
    return undefined;
  }

  function validateAddressOfSecondaryUserInMultiUserPlan() {
    const { multiUserPlan } = checkout;
    const secondaryUserSameAddressConfirmation =
      isMultiUserPlanEligible() && multiUserPlan?.toggled
        ? !multiUserPlan.secondaryUserSameAddressConfirmation // validate that the checkbox is checked
        : false; // no need to validate

    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentErrors: {
          ...enrollmentErrors,
          secondaryUserSameAddressConfirmation,
        },
      })
    );
  }

  function validateForm() {
    if (
      !promoCode.promoCodeIsVip &&
      isCollapsedPaymentEligible() &&
      hasEnrollmentErrors
    ) {
      return enrollmentErrors;
    }
    return undefined;
  }

  function applyError(error: errorConstants) {
    const errorMap = {
      [errorConstants.errorActiveSubscription]: {
        email: i18n.t(
          "enrollmentForm:inputFields:email:errorMessages:activeSubscription"
        ),
      },
      [errorConstants.errorPendingDeletionRequest]: {
        email: i18n.t(
          "enrollmentForm:inputFields:email:errorMessages:pendingDeletionRequest"
        ),
      },
      [errorConstants.errorInvalidEmail]: {
        email: i18n.t("enrollmentForm:inputFields:email:errorMessages:invalid"),
      },
      [errorConstants.errorTermAgreement]: {
        termAgreement: i18n.t("payment:terms:error"),
      },
    };

    if (error === errorConstants.noomClinicalIneligibleAge) {
      dispatch(failNoomClinicalAgeValidation());
      return true;
    }

    const errorState = errorMap[error];
    if (errorState) {
      dispatch(
        updatePaymentEnrollmentFormState({
          enrollmentErrors: {
            ...enrollmentErrors,
            ...errorState,
          },
        })
      );
      return true;
    }

    return false;
  }

  return {
    enrollmentInfo,
    setField: useCallbackRef(setField),
    updateEnrollmentInfo,

    validateForm: useCallbackRef(validateForm),

    enrollmentErrors,
    hasEnrollmentErrors,
    hasVisibleError: useCallbackRef(hasVisibleError),
    applyError: useCallbackRef(applyError),

    setTermAgreement: useCallbackRef(setTermAgreement),
    validateTermAgreement: useCallbackRef(validateTermAgreement),
    validateAddressOfSecondaryUserInMultiUserPlan: useCallbackRef(
      validateAddressOfSecondaryUserInMultiUserPlan
    ),
    showInvalidAgeModal,
  };
}

export function useEnroll() {
  const { enrollmentInfo } = useEnrollmentForm();
  const surveyAnswers = useSurveyAnswers();

  async function enroll(curriculum?: string) {
    // Track survey answer fields that do not exist and populate with default values
    const missingFields = [];
    let { gender, heightCm, weightKg, ageRange } = surveyAnswers;
    let genderMapping: string;
    if (!gender) {
      missingFields.push("gender");
      genderMapping = genderConstantsMapping.UNKNOWN;
    } else {
      genderMapping = genderConstantsMapping[gender[0]];
    }
    if (!heightCm) {
      missingFields.push("height");
      heightCm = 170;
    }
    if (!weightKg) {
      missingFields.push("weight");
      weightKg = 65;
    }
    if (!ageRange) {
      missingFields.push("age");
      ageRange = ["40s"];
    }
    // Fire off event only if missingFields is empty
    if (missingFields.length && !isHM()) {
      trackEvent("ProfileFieldMissing", {
        missingFields,
      });
    }
    const { email, name, password, phoneNumber } = enrollmentInfo;

    // Pass in data needed for CS
    const userProfileData: CollapsedEnrollmentData = {
      email,
      name,
      password,
      phoneNumber,
      language: getLanguage(),
      gender: genderMapping,
      heightInCm: heightCm,
      weightInKg: weightKg,
      age: parseInt(ageRange[0], 10),
      isNursing: isNursing(surveyAnswers),
    };

    if (!isHM()) {
      const weightLossData = prepareDataForBackend(surveyAnswers);
      if (weightLossData) {
        (userProfileData as any).weightLossData = weightLossData;
      }
    }

    return requestEnrollment({
      userProfileData,
      // Filter HW curriculum to maintain parity with prior impl
      // TODO: Check if the API is fine with this being passed
      curriculum: curriculum === "HW" ? undefined : curriculum,
    });
  }

  return {
    enroll: useCallbackRef(enroll),
  };
}

function getDateOfBirthError(dateOfBirth: string) {
  const dob = new Date(dateOfBirth);

  if (!dateOfBirth) {
    return i18n.t(
      "enrollmentForm:inputFields:dateOfBirth:errorMessages:invalid"
    );
  }

  // by checking the inputted {String} dob against the formatted version of {Date} dob
  // we can verify that the input was correctly formatted and the date exists
  if (dateOfBirth !== format("{MM}/{DD}/{YYYY}", dob)) {
    return i18n.t(
      "enrollmentForm:inputFields:dateOfBirth:errorMessages:invalidFormat"
    );
  }

  const today = new Date();
  if (dob > today) {
    return i18n.t(
      "enrollmentForm:inputFields:dateOfBirth:errorMessages:future"
    );
  }

  return "";
}

function validateEnrollmentInfo(
  enrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"],
  useNoomClinicalFields = false
) {
  const {
    email,
    password,
    confirmPassword,
    name,
    firstName,
    lastName,
    phoneNumber,
    phoneNumberCountryCode,
    dateOfBirth,
  } = enrollmentInfo;
  const enrollmentErrors: EnrollmentErrors = {
    name: false,
    email: false,
    password: false,
    confirmPassword: false,
    phoneNumber: false,
  };

  if (!useNoomClinicalFields) {
    if (!isName(name)) {
      enrollmentErrors.name = i18n.t<string>(
        "enrollmentForm:inputFields:name:errorMessages:required"
      );
    }
  } else {
    if (!isName(firstName)) {
      enrollmentErrors.firstName = i18n.t<string>(
        "enrollmentForm:inputFields:firstName:errorMessages:required"
      );
    }
    if (!isName(lastName)) {
      enrollmentErrors.lastName = i18n.t<string>(
        "enrollmentForm:inputFields:lastName:errorMessages:required"
      );
    }
    enrollmentErrors.dateOfBirth = getDateOfBirthError(dateOfBirth);
  }

  if (!isEmail(email)) {
    enrollmentErrors.email = i18n.t<string>(
      "enrollmentForm:inputFields:email:errorMessages:invalid"
    );
  }
  // Note(kevinh): bypass password validation on front end since the user's account is already assumed
  // to exist. CS will not validate password on their side in this case.
  if (!hasExistingAcccount()) {
    if (password?.length < 1) {
      enrollmentErrors.password = i18n.t<string>(
        "enrollmentForm:inputFields:password:errorMessages:required"
      );
    } else if (password?.length < 4) {
      enrollmentErrors.password = i18n.t<string>(
        "enrollmentForm:inputFields:password:errorMessages:minLength"
      );
    }
    if (password !== confirmPassword) {
      enrollmentErrors.confirmPassword = i18n.t<string>(
        "enrollmentForm:inputFields:confirmPassword:errorMessages:passwordsNotMatching"
      );
    }
  }
  if (
    shouldAskForPhoneNumber() &&
    !isValidNumber(phoneNumber, phoneNumberCountryCode)
  ) {
    enrollmentErrors.phoneNumber = i18n.t<string>(
      "enrollmentForm:inputFields:phoneNumber:errorMessages:invalid"
    );
  }
  return enrollmentErrors;
}
